import { notification } from 'antd';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import GachaForm, { FormValues } from '@/Gacha/components/GachaForm';
import { EmailTemplate } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';
import { convertPercentageToRate } from '@/service/numericOperation';

const UpdateGacha: React.FC<{}> = () => {
  const { gacha, fetchGacha } = useGacha();
  const [finishInitFetch, setFinishInitFetch] = useState<boolean>(false);
  const [releaseEmailTemplate, setReleaseEmailTemplate] = useState<EmailTemplate>({ htmlBody: '' });
  const [registrationEmailTemplate, setRegistrationEmailTemplate] = useState<EmailTemplate>({ htmlBody: '' });
  const api = createApiClientWithToken();
  const history = useHistory();

  useEffect(() => {
    const initialFetch = async () => {
      const findReleaseEmailTemplateRes = await api.findReleaseEmailTemplate(gacha.id).catch(() => {
        setReleaseEmailTemplate({ htmlBody: '' });

        return { htmlBody: '' };
      });

      setReleaseEmailTemplate(findReleaseEmailTemplateRes);

      const findRegistrationEmailTemplateRes = await api.findRegistrationEmailTemplate(gacha.id).catch(() => {
        setRegistrationEmailTemplate({ htmlBody: '' });

        return { htmlBody: '' };
      });

      setRegistrationEmailTemplate(findRegistrationEmailTemplateRes);
      setFinishInitFetch(true);
    };

    initialFetch();
  }, []);

  const onFinish = async (values: FormValues) => {
    const updateGachaRes = await api
      .updateGacha(gacha.id, {
        body: {
          name: values.name,
          upperTimes: values.upperTimes,
          winningRate: convertPercentageToRate(values.winningPercentage),
          iosDownloadUrl: values.iosDownloadUrl ?? null,
          androidDownloadUrl: values.androidDownloadUrl ?? null,
          canRetry: values.canRetry,
          retryInterval: values.retryInterval * 60 * 60,
          startTime: values.validPeriod ? values.validPeriod[0].toDate() : null,
          endTime: values.validPeriod ? values.validPeriod[1].toDate() : null,
          gameTitle: values.gameTitle ?? null,
          developerName: values.developerName ?? null,
          iconImageUrl: values.iconImageUrl ?? null,
          giftDetail: values.giftDetail ?? null,
          gameIntroduction: values.gameIntroduction ?? null,
          articleUrl: values.articleUrl ?? null,
          gameReleaseTime: values.gameReleaseTime ?? null,
          officialSiteUrl: values.officialSiteUrl ?? null,
        },
      })
      .catch(() => {
        notification.error({
          message: 'ガチャの更新に失敗しました。',
          description: `更新しようとしたガチャ名: ${values.name}`,
        });
      });

    if (!updateGachaRes) {
      return;
    }

    const editReleaseEmailTemplateRes = await api
      .editReleaseEmailTemplate(gacha.id, {
        body: {
          title: values.releaseNotificationEmailTitle,
          body: values.releaseNotificationEmailBody,
          htmlBody: values.releaseNotificationEmailHtmlBody,
        },
      })
      .catch(() => {
        notification.error({
          message: 'リリース通知のメール本文の更新に失敗しました',
          description: `更新しようとしたガチャ: ${values.name}`,
        });
      });

    const editRegistrationEmailTemplateRes = await api
      .editRegistrationEmailTemplate(gacha.id, {
        body: {
          title: values.registrationNotificationEmailTitle,
          body: values.registrationNotificationEmailBody,
          htmlBody: values.registrationNotificationEmailHtmlBody,
        },
      })
      .catch(() => {
        notification.error({
          message: '登録完了メール本文の更新に失敗しました',
          description: `更新しようとしたガチャ: ${values.name}`,
        });
      });

    if (!editRegistrationEmailTemplateRes || !editReleaseEmailTemplateRes) {
      return;
    }

    notification.success({
      message: 'ガチャの更新に成功しました',
      description: `ガチャ名: ${values.name}`,
    });

    await fetchGacha();
    history.push(`/gacha/${gacha.id}`);
  };

  if (!finishInitFetch) {
    return <div>loading</div>;
  }

  return (
    <div>
      <GachaForm
        onFinish={onFinish}
        releaseEmailTemplate={releaseEmailTemplate}
        registrationEmailTemplate={registrationEmailTemplate}
      />
    </div>
  );
};

export default UpdateGacha;
